import React, { useState, useEffect, useCallback } from 'react';
import { tasksApi } from '../api/tasksApi';
import type { Task } from '../types/contracts';
import Spinner     from '../components/Spinner';
import ErrorBanner from '../components/ErrorBanner';
import { haptic }  from '../telegram';

function fmtDate(iso?: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('ru', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

// ---- styles ----
const pageSt: React.CSSProperties  = { minHeight: '100vh', display: 'flex', flexDirection: 'column' };
const headerSt: React.CSSProperties = {
  position: 'sticky', top: 0, zIndex: 10,
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 16px',
  background: 'var(--tg-theme-header-bg-color)',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};
const h1St: React.CSSProperties    = { margin: 0, fontSize: 18, fontWeight: 600 };
const hintSt: React.CSSProperties  = { fontSize: 12, color: 'var(--tg-theme-hint-color)' };
const tabBarSt: React.CSSProperties = { display: 'flex', gap: 8, padding: '10px 16px' };
const scrollSt: React.CSSProperties = { flex: 1, overflowY: 'auto', padding: '0 16px 80px' };
const cardSt: React.CSSProperties  = {
  background: 'var(--tg-theme-secondary-bg-color)',
  borderRadius: 20, padding: 16, marginBottom: 12,
};
const actionsRowSt: React.CSSProperties = { display: 'flex', gap: 8, marginTop: 10 };
const btnPrimSt: React.CSSProperties = {
  flex: 1, padding: '8px 0', borderRadius: 12,
  background: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)',
  border: 'none', fontSize: 13, cursor: 'pointer',
};
const btnSecSt: React.CSSProperties = {
  padding: '8px 12px', borderRadius: 12,
  background: 'rgba(255,255,255,0.08)', color: 'var(--tg-theme-text-color)',
  border: 'none', fontSize: 13, cursor: 'pointer',
};
const btnDangerSt: React.CSSProperties = {
  padding: '8px 12px', borderRadius: 12,
  background: 'rgba(255,69,58,0.2)', color: 'var(--tg-theme-destructive-text-color)',
  border: 'none', fontSize: 13, cursor: 'pointer',
};
const reschRowSt: React.CSSProperties = { display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' };
const dateInputSt: React.CSSProperties = {
  flex: 1, borderRadius: 12, padding: '8px 12px',
  background: 'var(--tg-theme-bg-color)', color: 'var(--tg-theme-text-color)',
  border: '1px solid rgba(255,255,255,0.12)', fontSize: 13, outline: 'none',
};
const centeredSt: React.CSSProperties = { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 };
const emptyTextSt: React.CSSProperties = { textAlign: 'center', color: 'var(--tg-theme-hint-color)', fontSize: 14, marginTop: 48 };
const accentSt: React.CSSProperties = { color: 'var(--tg-theme-accent-text-color)', fontSize: 12 };

export default function Tasks() {
  const [tasks,       setTasks]       = useState<Task[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState<string | null>(null);
  const [tab,         setTab]         = useState<'todo' | 'done'>('todo');
  const [reschedId,   setReschedId]   = useState<string | null>(null);
  const [reschedDate, setReschedDate] = useState('');

  const load = useCallback(async () => {
    try { setLoading(true); setError(null); setTasks(await tasksApi.list()); }
    catch (e) { setError(String(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleComplete = async (id: string) => {
    haptic('medium');
    setTasks((p) => p.map((t) => t.id === id ? { ...t, status: 'done' } : t));
    try { await tasksApi.complete(id); }
    catch { setTasks((p) => p.map((t) => t.id === id ? { ...t, status: 'todo' } : t)); }
  };

  const handleCancel = async (id: string) => {
    haptic('medium');
    if (!window.confirm('Отменить задачу?')) return;
    setTasks((p) => p.filter((t) => t.id !== id));
    try { await tasksApi.cancel(id); } catch { load(); }
  };

  const handleReschedule = async () => {
    if (!reschedId || !reschedDate) return;
    try {
      await tasksApi.reschedule(reschedId, new Date(reschedDate).toISOString());
      setReschedId(null); setReschedDate('');
      haptic('medium'); load();
    } catch (e) { setError(String(e)); }
  };

  const tabActiveSt = (active: boolean): React.CSSProperties => ({
    padding: '6px 18px', borderRadius: 20,
    background: active ? 'var(--tg-theme-button-color)' : 'rgba(255,255,255,0.08)',
    color: active ? 'var(--tg-theme-button-text-color)' : 'var(--tg-theme-hint-color)',
    border: 'none', fontSize: 14, cursor: 'pointer',
  });

  if (loading) return <div style={centeredSt}><Spinner size={32} /></div>;
  if (error)   return <ErrorBanner message={error} onRetry={load} />;

  const filtered = tasks.filter((t) => t.status === tab);
  const todoCount = tasks.filter((t) => t.status === 'todo').length;

  return (
    <div style={pageSt}>
      <div style={headerSt}>
        <h1 style={h1St}>Задачи</h1>
        <span style={hintSt}>{todoCount} акт.</span>
      </div>

      <div style={tabBarSt}>
        <button style={tabActiveSt(tab === 'todo')} onClick={() => { setTab('todo'); haptic('light'); }}>Пендинг</button>
        <button style={tabActiveSt(tab === 'done')} onClick={() => { setTab('done'); haptic('light'); }}>Готово</button>
      </div>

      <div style={scrollSt}>
        {filtered.length === 0 && <p style={emptyTextSt}>Нет задач</p>}
        {filtered.map((task) => (
          <div key={task.id} style={cardSt}>
            <p style={{ margin: 0, fontWeight: 500, textDecoration: task.status === 'done' ? 'line-through' : 'none', opacity: task.status === 'done' ? 0.5 : 1 }}>
              {task.title}
            </p>
            {task.notes && <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--tg-theme-hint-color)' }}>{task.notes}</p>}
            {task.due_at && <p style={accentSt}>⏰ {fmtDate(task.due_at)}</p>}

            {task.status === 'todo' && (
              <div style={actionsRowSt}>
                <button style={btnPrimSt} onClick={() => handleComplete(task.id)}>✓ Готово</button>
                <button style={btnSecSt}  onClick={() => { setReschedId(task.id); setReschedDate(''); }}>📅</button>
                <button style={btnDangerSt} onClick={() => handleCancel(task.id)}>×</button>
              </div>
            )}

            {reschedId === task.id && (
              <div style={reschRowSt}>
                <input type="datetime-local" style={dateInputSt}
                  value={reschedDate}
                  onChange={(e) => setReschedDate(e.target.value)} />
                <button style={btnPrimSt} onClick={handleReschedule}>✔</button>
                <button style={btnSecSt}  onClick={() => setReschedId(null)}>×</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
